import type { BoxTruckVerification } from '../services/boxTruckLoadsApi'

/**
 * What a My Drivers row can actually DO, decided in one place.
 *
 * Location, bids and App Access are all keyed to the driver's standalone ACCOUNT. A driver an HR added from
 * Profiles has a fleet record and no account, so `userId` on that row is the fleet record id — every
 * account-scoped endpoint resolves through live claims and answers 404 for it. Offering a control that is
 * guaranteed to fail is worse than not offering it: the operator reads the failure as the platform being
 * broken rather than as the driver not having an app account.
 *
 * An ABSENT flag means account-backed, so rows from a server that predates it behave exactly as before.
 */
export const hasAppAccount = (d: Pick<BoxTruckVerification, 'appAccountLinked'>): boolean => d.appAccountLinked !== false

/** A location can live on an account or on an operator-owned accountless fleet record. */
export const canEditLocation = (d: Pick<BoxTruckVerification, 'appAccountLinked' | 'claimDriverId'>): boolean => hasAppAccount(d) || !!d.claimDriverId

/**
 * Can this driver be picked for a load?
 *
 * A stored position is necessary but not sufficient: the bid path resolves through live claims, so an
 * accountless row is filtered out server-side AFTER being picked — which reads to the dispatcher as their
 * selection quietly not counting. A fleet-login driver CAN hold a fix (their own app writes it under the
 * fleet record id), so the location alone never proved an account existed.
 */
export const isSelectable = (d: Pick<BoxTruckVerification, 'lastLocation' | 'appAccountLinked'>, requireAppAccount = true): boolean =>
    !!d.lastLocation && (!requireAppAccount || hasAppAccount(d))

/**
 * Is there anything for the assignments endpoint to resolve?
 *
 * It answers from the caller's CLAIM on the driver — the owner and the vehicle both hang off that record —
 * so a driver this operator has not added has nothing to read and the request comes back empty every time.
 * That matters on the cross-tenant Driver Verifications queue, where most rows are exactly that and an
 * operator pages through them: asking is a round trip whose answer is known in advance.
 *
 * My Drivers never needs this — every row there is claimed by definition.
 */
export const hasResolvableAssignments = (d: Pick<BoxTruckVerification, 'claimStatus'>): boolean => d.claimStatus === 'added'
